import { useAsyncData, useRoute, useRuntimeConfig } from '#imports'
import { computed, ref, watch } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { transformReviewsResponse } from '../utils/transformReviewsResponse'

import { useReviewsQueryParams } from './useReviewsQueryParams'

function useFetchReviews(cursor = '') {
  const $httpFetch = useHttpFetch()
  const { FF_ALL_REVIEWS_PAGE_20_PER_PAGE } = useRuntimeConfig().public

  const queryParams = useReviewsQueryParams(() => ({
    pageSize: FF_ALL_REVIEWS_PAGE_20_PER_PAGE ? 20 : undefined,
    cursor,
  }))

  return useAsyncData(
    getAsyncDataKey('reviews-list', queryParams.value),
    () => {
      return $httpFetch('/reviews/v1/products/reviews', {
        query: queryParams.value,
      })
    },
    {
      transform: (response) => {
        return {
          ...response,
          results: transformReviewsResponse(response.results),
        }
      },
    },
  )
}

export function useReviewsList() {
  const route = useRoute()
  const logger = useLogger()

  const cursor = ref()

  const { data, error, pending, execute } = useFetchReviews(cursor.value)

  const reviewsResponse = computed(() => ({
    ...data.value,
    results: data.value?.results ?? [],
  }))

  const isReviewsFetchPending = computed(() => pending.value)

  watch(
    error,
    () => {
      if (!(error.value instanceof Error)) return

      logger.error(`[REVIEWS][${route.name?.toString()}]`, {
        error: error.value,
        owners: ['bot-squad-content-front'],
      })
    },
    { immediate: true },
  )

  watch(
    data,
    (newData) => {
      cursor.value = newData?.nextCursor
    },
    { immediate: true },
  )

  watch(
    () => route.query,
    async () => {
      await execute()
    },
  )

  return { isReviewsFetchPending, reviewsResponse }
}
